import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Input, Select, Button, Space, Tag, Spin, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import {
  PrimeArtifactWorkflowItem,
  WorkflowStatistics,
} from '../../types/primeArtifact';
import { WorkflowStatistics as WorkflowStatisticsComponent } from './WorkflowStatistics';
import { ArtifactDetailsPanel } from './ArtifactDetailsPanel';
import { PrimeArtifact } from '../../types/primeArtifact';
import styles from './PrimeArtifactWorkflowListView.module.css';

const { Option } = Select;

interface PrimeArtifactWorkflowListViewProps {
  initialSelectedArtifactId?: string;
  onArtifactSelect?: (artifact: PrimeArtifactWorkflowItem) => void;
  onCreateArtifactType?: () => void;
  onCreateGlobalProperty?: () => void;
}

type FilterValue = string | number | null;

const WORKFLOW_STATUSES = ['IDENTIFIED', 'DEFINED', 'DESIGNED', 'IMPLEMENTED', 'VALIDATED'];

const WORKFLOW_STATUS_COLORS: Record<string, string> = {
  IDENTIFIED: 'default',
  DEFINED: 'processing',
  DESIGNED: 'processing',
  IMPLEMENTED: 'warning',
  VALIDATED: 'success',
};

export const PrimeArtifactWorkflowListView: React.FC<PrimeArtifactWorkflowListViewProps> = ({
  initialSelectedArtifactId,
  onArtifactSelect,
  onCreateArtifactType,
  onCreateGlobalProperty,
}) => {
  console.log('[PrimeArtifactWorkflowListView] Component rendering');
  const [loading, setLoading] = useState(true);
  const [artifacts, setArtifacts] = useState<PrimeArtifactWorkflowItem[]>([]);
  const [statistics, setStatistics] = useState<WorkflowStatistics | null>(null);
  const [selectedArtifact, setSelectedArtifact] = useState<PrimeArtifactWorkflowItem | null>(null);
  
  // Filter states
  const [nameFilter, setNameFilter] = useState<FilterValue>(null);
  const [typeFilter, setTypeFilter] = useState<FilterValue>(null);
  const [templateFilter, setTemplateFilter] = useState<FilterValue>(null);
  const [statusFilter, setStatusFilter] = useState<FilterValue>(null);
  const [propertiesCountFilter, setPropertiesCountFilter] = useState<FilterValue>(null);
  const [statesCountFilter, setStatesCountFilter] = useState<FilterValue>(null);
  const [transitionsCountFilter, setTransitionsCountFilter] = useState<FilterValue>(null);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      console.log('[PrimeArtifactWorkflowListView] Loading workflow list...');
      const response = await PrimeArtifactService.getWorkflowList();
      console.log('[PrimeArtifactWorkflowListView] Loaded workflow list:', response);
      setArtifacts(response.artifacts);
      setStatistics(response.statistics);
      
      // Set initial selection if provided
      if (initialSelectedArtifactId) {
        const initial = response.artifacts.find(a => a.id === initialSelectedArtifactId);
        if (initial) {
          setSelectedArtifact(initial);
        }
      }
    } catch (error) {
      console.error('[PrimeArtifactWorkflowListView] Error loading workflow list:', error);
      message.error('Failed to load workflow list');
    } finally {
      setLoading(false);
    }
  }, [initialSelectedArtifactId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Get unique values for filters
  const uniqueTypes = useMemo(() => {
    const types = new Set(artifacts.map(a => a.artifact_type_name).filter(Boolean));
    return Array.from(types).sort();
  }, [artifacts]);

  const uniqueTemplates = useMemo(() => {
    const templates = new Set(artifacts.map(a => a.state_template_name).filter(Boolean));
    return Array.from(templates).sort();
  }, [artifacts]);

  // Filter artifacts
  const filteredArtifacts = useMemo(() => {
    return artifacts.filter((artifact) => {
      if (nameFilter && !artifact.name.toLowerCase().includes(String(nameFilter).toLowerCase())) {
        return false;
      }
      if (typeFilter && artifact.artifact_type_name !== typeFilter) {
        return false;
      }
      if (templateFilter && artifact.state_template_name !== templateFilter) {
        return false;
      }
      if (statusFilter && artifact.development_status !== statusFilter) {
        return false;
      }
      if (propertiesCountFilter !== null && artifact.properties_count !== Number(propertiesCountFilter)) {
        return false;
      }
      if (statesCountFilter !== null && artifact.states_count !== Number(statesCountFilter)) {
        return false;
      }
      if (transitionsCountFilter !== null && artifact.transitions_count !== Number(transitionsCountFilter)) {
        return false;
      }
      return true;
    });
  }, [artifacts, nameFilter, typeFilter, templateFilter, statusFilter, propertiesCountFilter, statesCountFilter, transitionsCountFilter]);

  // Table columns
  const columns: ColumnsType<PrimeArtifactWorkflowItem> = [
    {
      title: 'Prime Artifact',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search name"
            value={selectedKeys[0]}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
            }}
            onPressEnter={() => {
              confirm();
              setNameFilter(selectedKeys[0] as string);
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setNameFilter(selectedKeys[0] as string);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                setNameFilter(null);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: 'Artifact Type',
      dataIndex: 'artifact_type_name',
      key: 'artifact_type_name',
      sorter: (a, b) => (a.artifact_type_name || '').localeCompare(b.artifact_type_name || ''),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Select type"
            value={selectedKeys[0] as string}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
            }}
            style={{ width: 188, marginBottom: 8 }}
            allowClear
          >
            {uniqueTypes.map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setTypeFilter(selectedKeys[0] as string);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Filter
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                setTypeFilter(null);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Global Template',
      dataIndex: 'state_template_name',
      key: 'state_template_name',
      sorter: (a, b) => (a.state_template_name || '').localeCompare(b.state_template_name || ''),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Select template"
            value={selectedKeys[0] as string}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
            }}
            style={{ width: 188, marginBottom: 8 }}
            allowClear
          >
            {uniqueTemplates.map((template) => (
              <Option key={template} value={template}>
                {template}
              </Option>
            ))}
          </Select>
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setTemplateFilter(selectedKeys[0] as string);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Filter
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                setTemplateFilter(null);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (text: string) => text || '-',
    },
    {
      title: '# Properties',
      dataIndex: 'properties_count',
      key: 'properties_count',
      sorter: (a, b) => a.properties_count - b.properties_count,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            type="number"
            placeholder="Filter by count"
            value={selectedKeys[0] as string}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
            }}
            onPressEnter={() => {
              confirm();
              setPropertiesCountFilter(selectedKeys[0] ? Number(selectedKeys[0]) : null);
            }}
            style={{ width: 188, marginBottom: 8 }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setPropertiesCountFilter(selectedKeys[0] ? Number(selectedKeys[0]) : null);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Filter
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                setPropertiesCountFilter(null);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      align: 'right',
    },
    {
      title: '# States',
      dataIndex: 'states_count',
      key: 'states_count',
      sorter: (a, b) => a.states_count - b.states_count,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            type="number"
            placeholder="Filter by count"
            value={selectedKeys[0] as string}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
            }}
            onPressEnter={() => {
              confirm();
              setStatesCountFilter(selectedKeys[0] ? Number(selectedKeys[0]) : null);
            }}
            style={{ width: 188, marginBottom: 8 }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setStatesCountFilter(selectedKeys[0] ? Number(selectedKeys[0]) : null);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Filter
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                setStatesCountFilter(null);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      align: 'right',
    },
    {
      title: '# Transitions',
      dataIndex: 'transitions_count',
      key: 'transitions_count',
      sorter: (a, b) => a.transitions_count - b.transitions_count,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            type="number"
            placeholder="Filter by count"
            value={selectedKeys[0] as string}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
            }}
            onPressEnter={() => {
              confirm();
              setTransitionsCountFilter(selectedKeys[0] ? Number(selectedKeys[0]) : null);
            }}
            style={{ width: 188, marginBottom: 8 }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setTransitionsCountFilter(selectedKeys[0] ? Number(selectedKeys[0]) : null);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Filter
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                setTransitionsCountFilter(null);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      align: 'right',
    },
    {
      title: 'Status',
      dataIndex: 'development_status',
      key: 'development_status',
      sorter: (a, b) => a.development_status.localeCompare(b.development_status),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Select status"
            value={selectedKeys[0] as string}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
            }}
            style={{ width: 188, marginBottom: 8 }}
            allowClear
          >
            {WORKFLOW_STATUSES.map((status) => (
              <Option key={status} value={status}>
                {status}
              </Option>
            ))}
          </Select>
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setStatusFilter(selectedKeys[0] as string);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Filter
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                setStatusFilter(null);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (status: string) => (
        <Tag color={WORKFLOW_STATUS_COLORS[status]}>{status}</Tag>
      ),
    },
  ];

  const handleRowClick = (record: PrimeArtifactWorkflowItem) => {
    setSelectedArtifact(record);
    onArtifactSelect?.(record);
  };

  const handleStatusClick = (status: string) => {
    setStatusFilter(status);
  };

  const handleEdit = useCallback((_artifact: PrimeArtifact) => {
    // Navigate to artifact details or open edit dialog
    message.info('Edit artifact (to be implemented)');
  }, []);

  if (loading) {
    return (
      <div className={styles.workflowListView}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.workflowListView}>
      {statistics && (
        <WorkflowStatisticsComponent
          statistics={statistics}
          onStatusClick={handleStatusClick}
        />
      )}
      
      <div className={styles.workflowListSplitPanel}>
        <div className={styles.workflowListLeftPanel}>
          <div className={styles.workflowListHeader}>
            <h3>Prime Artifacts</h3>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void loadData()}
              size="small"
            >
              Refresh
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={filteredArtifacts}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: {
                cursor: 'pointer',
                backgroundColor: selectedArtifact?.id === record.id ? '#e6f7ff' : undefined,
              },
            })}
            pagination={{ 
              pageSize: 100,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} artifacts`,
              pageSizeOptions: ['50', '100', '200', '500']
            }}
            size="small"
            scroll={{ y: 'calc(100vh - 300px)' }}
          />
        </div>
        
        <div className={styles.workflowListRightPanel}>
          <ArtifactDetailsPanel
            selectedArtifact={selectedArtifact}
            onArtifactUpdated={(updatedArtifact) => {
              // Update the artifact in the list
              setArtifacts(prev => prev.map(a => 
                a.id === updatedArtifact.id ? updatedArtifact : a
              ));
              setSelectedArtifact(updatedArtifact);
            }}
            onCreateArtifactType={onCreateArtifactType}
            onCreateGlobalProperty={onCreateGlobalProperty}
          />
        </div>
      </div>
    </div>
  );
};

